"use client"
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Modal, ModalContent } from "@heroui/modal";
import {useMonitoring} from "@/components/monitoring";

function useIsSmallDevice() {
  const [isSmallDevice, setIsSmallDevice] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const mediaQuery = window.matchMedia("only screen and (max-width : 768px)");
    const update = () => setIsSmallDevice(mediaQuery.matches);

    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, []);

  return isSmallDevice;
}

const ModalContext = createContext<{
  show: (options: {component: React.ElementType; data?: object}) => Promise<{accepted: boolean; rejected: boolean}>;
}>({
  show: () => Promise.resolve({ accepted: false, rejected: false }),
});

export function ModalProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState(null);
  const isSmallDevice = useIsSmallDevice();
  const promiseRef = useRef({
    promise: Promise.resolve({ accepted: false, rejected: false }),
    resolve: () => {},
  });
  const componentRef = useRef(null);
  const onClose = (isAccepted, data) => {
    setIsOpen(false);
    promiseRef.current.resolve({
      accepted: isAccepted,
      rejected: !isAccepted,
      data,
    });
  };

  const show = ({ component, data: _data }) => {
    componentRef.current = component;
    setData(_data);
    setIsOpen(true);
    // track(`Modal Opened ${component?.name || "unknown modal"}`)
    if (!promiseRef.current) return null;
    promiseRef.current.promise = new Promise((resolve) => {
      promiseRef.current.resolve = resolve;
    });
    return promiseRef.current.promise;
  };

  return (
    <ModalContext.Provider value={{ show }}>
      <>
        <Modal
          closeButton
          backdrop="blur"
          aria-labelledby="modal-title"
          isOpen={isOpen}
          data-ui={componentRef?.current?.name || "unknown modal"}
          className={data?.className ?? ""}
          size={isSmallDevice ? "full" : (data?.size ?? undefined)}
          scrollBehavior={data?.scrollBehavior ?? undefined}
          onOpenChange={(aa) => {}}
          onClose={(aa) => {
            onClose(false);
          }}
        >
          <ModalContent>
            {() =>
              componentRef.current && (
                <componentRef.current
                  onClose={(data) => onClose(false, data)}
                  onAccept={(data) => onClose(true, data)}
                  data={data}
                />
              )
            }
          </ModalContent>
        </Modal>
        {children}
      </>
    </ModalContext.Provider>
  );
}

export const useModal = () => {
  return useContext(ModalContext);
};
